

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchInput from '../base/SearchInput';
import Button from '../base/Button';
import SearchModal from './SearchModal';

interface HeaderProps {
  onTrackSelect: (track: any) => void;
}

export default function Header({ onTrackSelect }: HeaderProps) {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (query: string) => {
    if (query.trim()) {
      setIsSearchModalOpen(true);
    }
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    setIsUserMenuOpen(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setIsUserMenuOpen(false);
  };

  const isActivePage = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-8">
              <div 
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => navigate('/')}
              >
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-cyan-500 to-blue-600 rounded-xl rotate-12 transform"></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-500 via-pink-500 to-red-500 rounded-xl -rotate-12 transform opacity-80"></div>
                  <div className="relative z-10 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-lg">
                    <div className="w-3 h-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full"></div>
                    <div className="absolute top-1 right-1 w-1 h-1 bg-emerald-500 rounded-full"></div>
                    <div className="absolute bottom-1 left-1 w-1 h-1 bg-blue-500 rounded-full"></div>
                  </div>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent tracking-wide" style={{ fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif', fontWeight: '800', letterSpacing: '0.05em' }}>
                  SoundWave
                </span>
              </div>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-6">
                <button 
                  onClick={() => navigate('/')}
                  className={`font-medium cursor-pointer transition-colors ${
                    isActivePage('/') ? 'text-purple-600' : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  Home
                </button>
                <button 
                  onClick={() => navigate('/browse')}
                  className={`font-medium cursor-pointer transition-colors ${
                    isActivePage('/browse') ? 'text-purple-600' : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  Browse
                </button>
                <button 
                  onClick={() => navigate('/radio')}
                  className={`font-medium cursor-pointer transition-colors ${
                    isActivePage('/radio') ? 'text-purple-600' : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  Radio
                </button>
                <button 
                  onClick={() => navigate('/library')}
                  className={`font-medium cursor-pointer transition-colors ${
                    isActivePage('/library') ? 'text-purple-600' : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  Library
                </button>
              </nav>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md mx-8">
              <SearchInput 
                onSearch={handleSearch}
                placeholder="Search for songs, artists, albums..."
              />
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-4">
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
                  >
                    <img
                      src="https://readdy.ai/api/search-image?query=professional%20user%20avatar%20portrait%20with%20friendly%20expression%2C%20modern%20photography%20style%2C%20neutral%20background&width=100&height=100&seq=user-avatar&orientation=squarish"
                      alt="User"
                      className="w-8 h-8 rounded-full object-cover object-top"
                    />
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                      <button 
                        onClick={() => {
                          navigate('/library');
                          setIsUserMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-user-line mr-2"></i>Profile
                      </button>
                      <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        <i className="ri-settings-line mr-2"></i>Settings
                      </a>
                      <button 
                        onClick={() => {
                          navigate('/upload');
                          setIsUserMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-upload-line mr-2"></i>Upload Music
                      </button>
                      <hr className="my-2" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button variant="ghost" onClick={handleLogin}>
                    Sign In
                  </Button>
                  <Button onClick={handleLogin}>
                    Sign Up
                  </Button>
                </div>
              )}

              <button className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer md:hidden">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onTrackSelect={onTrackSelect}
      />
    </>
  );
}

